import { SlidersHorizontal, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  countActive,
  refinementsFor,
  type SwipeRefinementSelection,
} from "./refinements";
import type { SwipeScenario } from "./types";

/** Radix Select has no empty-string value, so "any" needs a sentinel. */
const ANY = "__any__";

/**
 * Runtime narrowing of a scenario's queue.
 *
 * This is the half of "configurable" that does not need a deploy: the scenario
 * supplies the question and the answers, and this supplies who gets asked.
 * Selections live in the URL, so "ICP fit, ecosystem contacts, quiet for a
 * year" is a bookmark rather than a new scenario.
 *
 * It lives in a popover rather than a bar because it is occasional and the
 * answers are constant: a permanent row of selects cost ~60px of vertical
 * space, which on a 667px phone is the difference between the third answer
 * being under the thumb and being under the nav bar.
 */
export const SwipeFilterBar = ({
  scenario,
  selection,
  onChange,
}: {
  scenario: SwipeScenario;
  selection: SwipeRefinementSelection;
  onChange: (next: SwipeRefinementSelection) => void;
}) => {
  const refinements = refinementsFor(scenario);
  if (refinements.length === 0) return null;

  const active = countActive(scenario, selection);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={active > 0 ? "secondary" : "ghost"}
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs"
          // The visible label is hidden on a phone to fit the header, which
          // would otherwise leave the button with no accessible name at all.
          aria-label={
            active > 0 ? `Who gets asked, ${active} active` : "Who gets asked"
          }
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          {/* Icon-only on a phone: this label, the back label and the counters
              together overflowed a 390px header, and the counters lost. */}
          <span className="hidden sm:inline">Who gets asked</span>
          {active > 0 && (
            <Badge variant="default" className="ml-0.5 h-4 px-1 text-[10px]">
              {active}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2 p-3">
        <p className="text-xs text-muted-foreground">
          Narrows this scenario's queue. Never widens it — the scenario's own
          filter always wins.
        </p>
        {refinements.map((refinement) => (
          <Select
            key={refinement.id}
            value={selection[refinement.id] ?? ANY}
            onValueChange={(value) =>
              onChange({
                ...selection,
                [refinement.id]: value === ANY ? undefined : value,
              })
            }
          >
            <SelectTrigger
              size="sm"
              className="w-full text-xs"
              aria-label={refinement.label}
            >
              <SelectValue placeholder={refinement.label}>
                {refinement.label}:{" "}
                {refinement.choices.find(
                  (choice) => choice.value === selection[refinement.id],
                )?.label ?? "any"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{refinement.label}: any</SelectItem>
              {refinement.choices.map((choice) => (
                <SelectItem key={choice.value} value={choice.value}>
                  {choice.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        {active > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            onClick={() => onChange({})}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
